import { useEffect, useMemo, useRef, useState } from 'react'
import ApiService from '@services/api'
import { useNotification } from '@context/NotificationContext'
import DeadlineSimulationResult from './components/DeadlineSimulationResult'
import {
  MAX_SIMULATION_IMPORT_BYTES, MAX_SIMULATION_TEXT_LENGTH as MAX_TEXT_LENGTH,
  INITIAL_SIMULATION_FORM as INITIAL_FORM, createSimulationEvidence,
  createSimulationSnapshot, downloadSimulationEvidence, hashSimulationBytes, parseSimulationImport,
  verifySimulationReceipt,
} from './simulationCollection'

function Field({ label, hint, children, required = false, className = '' }) {
  return (
    <label className={`rules-field ${className}`.trim()}>
      <span className="rules-field-label">
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  )
}

export default function DeadlineSimulatorPage() {
  const notify = useNotification()
  const [form, setForm] = useState(INITIAL_FORM)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [lastPayload, setLastPayload] = useState(null)
  const [textTouched, setTextTouched] = useState(false)
  const [importedCases, setImportedCases] = useState([])
  const [selectedCaseId, setSelectedCaseId] = useState('')
  const [evidenceFiles, setEvidenceFiles] = useState([])
  const [importing, setImporting] = useState(false)
  const activeRequest = useRef(null)
  const mounted = useRef(true)
  const busy = loading || importing
  const importedCase = importedCases.find((item) => item.caseId === selectedCaseId)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false; activeRequest.current = null }
  }, [])

  const textLength = form.text.length
  const isTextMissing = textTouched && form.text.trim().length === 0
  const isTextOverLimit = textLength > MAX_TEXT_LENGTH
  const canSubmit = useMemo(
    () => form.text.trim().length > 0 && form.text.length <= MAX_TEXT_LENGTH
      && (!form.court.trim() || Boolean(form.instance))
      && (!form.city.trim() || /^[A-Z]{2}$/.test(form.state.trim())) && !busy,
    [form.text, form.court, form.instance, form.state, form.city, busy],
  )

  const handleChange = (field) => (event) => {
    if (activeRequest.current || importing) return
    const value = field === 'state' ? event.target.value.toUpperCase() : event.target.value
    setForm((current) => ({ ...current, [field]: value }))
    setResult(null)
    setLastPayload(null)
  }

  const selectImportedCase = (caseId, cases = importedCases) => {
    if (activeRequest.current) return
    const selected = cases.find((item) => item.caseId === caseId)
    if (!selected) return
    setSelectedCaseId(caseId)
    setForm({ ...selected.form })
    setResult(null)
    setLastPayload(null)
    setTextTouched(false)
  }

  const handleImport = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || activeRequest.current || importing) return
    if (file.size > MAX_SIMULATION_IMPORT_BYTES) {
      notify.showError(`O arquivo excede o limite de ${MAX_SIMULATION_IMPORT_BYTES.toLocaleString('pt-BR')} bytes.`)
      return
    }
    setImporting(true)
    try {
      const bytes = await file.arrayBuffer()
      const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
      const cases = parseSimulationImport(text, file.name, await hashSimulationBytes(bytes))
      if (!mounted.current) return
      setImportedCases(cases)
      selectImportedCase(cases[0].caseId, cases)
      notify.showSuccess(`${cases.length.toLocaleString('pt-BR')} caso(s) importado(s). Confira os metadados antes de simular.`)
    } catch (error) {
      if (mounted.current) notify.showError(error.message || 'Não foi possível importar os casos.')
    } finally {
      if (mounted.current) setImporting(false)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (activeRequest.current || importing) return

    setTextTouched(true)

    if (!form.text.trim()) {
      notify.showError('Informe o texto da comunicação para executar a simulação.')
      return
    }

    if (form.text.length > MAX_TEXT_LENGTH) {
      notify.showError(`O texto excede o limite máximo de ${MAX_TEXT_LENGTH.toLocaleString('pt-BR')} caracteres.`)
      return
    }

    if (form.court.trim() && !form.instance) {
      notify.showError('Selecione a instância do processo para consultar o calendário aplicável.')
      return
    }

    if (form.city.trim() && !/^[A-Z]{2}$/.test(form.state.trim())) {
      notify.showError('Informe a UF do município para consultar os eventos locais.')
      return
    }

    if (!globalThis.crypto?.randomUUID) {
      notify.showError('Abra o simulador em HTTPS ou localhost para conferir a integridade da coleta.')
      return
    }
    const requestId = globalThis.crypto.randomUUID()
    activeRequest.current = requestId
    setLoading(true)
    setResult(null)
    setLastPayload(null)
    try {
      const snapshot = await createSimulationSnapshot(form, importedCase, requestId, new Date().toISOString())
      let response
      try { response = await ApiService.simulateDeadline(snapshot.payload) } catch (error) {
        response = { success: false, error: error.message || 'A comunicação com a API falhou.' }
      }
      if (!mounted.current || activeRequest.current !== requestId) return
      const integrity = response.success ? await verifySimulationReceipt(snapshot, response.data)
        : { verified: false, errors: [response.error || 'Não foi possível simular o DeadlineEngine.'] }
      if (!mounted.current || activeRequest.current !== requestId) return
      const evidence = createSimulationEvidence(snapshot, response, integrity, new Date().toISOString())
      setEvidenceFiles((current) => [...current, evidence])
      try { downloadSimulationEvidence(evidence) } catch {
        notify.showWarning('A evidência está disponível nesta sessão. Use o botão de download para guardá-la antes de sair.')
      }
      if (!integrity.verified) {
        notify.showError(integrity.errors.join(' '))
        return
      }
      setResult(response.data)
      setLastPayload(snapshot.payload)
      notify.showSuccess('Simulação conferida. A evidência deste caso está disponível em arquivo exclusivo; confira o download.')
    } catch (error) {
      if (mounted.current) notify.showError(error.message || 'Não foi possível conferir a simulação.')
    } finally {
      if (activeRequest.current === requestId) activeRequest.current = null
      if (mounted.current) setLoading(false)
    }
  }

  const handleReset = () => {
    if (activeRequest.current || importing) return
    setForm(INITIAL_FORM)
    setResult(null)
    setLastPayload(null)
    setTextTouched(false)
    setSelectedCaseId('')
  }

  return (
    <div className="main-content rules-config-page deadline-simulator-page">
      <div className="rules-layout-grid simulator-grid deadline-simulator-grid">
        <form className="deadline-form-stack" onSubmit={handleSubmit}>
          <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'contents' }}>
          <section className="rules-card deadline-form-card">
            <div className="rules-card-header">
              <div>
                <h3>Importar caso ou lote</h3>
                <p>Use o cohort congelado ou um payload JSON completo. O texto original é preservado sem cortes.</p>
              </div>
            </div>
            <div className="rules-card-body rules-form-grid">
              <Field label="Arquivo JSON" hint={`Até ${(MAX_SIMULATION_IMPORT_BYTES / 1024 / 1024).toLocaleString('pt-BR')} MiB. Importar preenche o formulário; a simulação começa ao confirmar o envio.`}>
                <input type="file" accept=".json,application/json" onChange={handleImport} />
              </Field>
              {importedCases.length ? <Field label="Caso importado">
                <select value={selectedCaseId} onChange={(event) => selectImportedCase(event.target.value)}>
                  <option value="">Selecione um caso</option>
                  {importedCases.map((item) => <option key={item.caseId} value={item.caseId}>
                    Caso {item.caseId} — {item.form.processNumber || 'processo não informado'}
                  </option>)}
                </select>
              </Field> : null}
              {importedCase ? <p>
                Caso {importedCase.caseId} de {importedCase.fileName}.
                {' '}{JSON.stringify(form) === JSON.stringify(importedCase.form) ? 'Entrada original preservada.' : 'O formulário contém alterações; elas serão registradas na evidência.'}
              </p> : null}
              {importedCase?.context.externalContext ? <p>O contexto externo importado será tratado como informação não verificada.</p> : null}
              {importedCase?.context.dataJudContext ? <p>O contexto DataJud original será preservado na evidência. A entrada manual não aceita esse contexto como informação verificada.</p> : null}
            </div>
          </section>
          <section className="rules-card deadline-form-card">
            <div className="rules-card-header">
              <div>
                <h3>Dados do processo</h3>
                <p>Informe os dados de identificação processual usados pelo motor de prazos.</p>
              </div>
            </div>

            <div className="rules-card-body rules-form-grid two-columns">
              <Field label="Número do processo">
                <input type="text" value={form.processNumber} onChange={handleChange('processNumber')} placeholder="Número do processo" />
              </Field>

              <Field label="Tribunal">
                <input type="text" value={form.court} onChange={handleChange('court')} placeholder="Ex.: TJSP" />
              </Field>

              <Field label="Instância" required={Boolean(form.court.trim())} hint="Selecione a instância para usar as fontes de calendário aplicáveis.">
                <select value={form.instance} onChange={handleChange('instance')} required={Boolean(form.court.trim())}>
                  <option value="">Selecione a instância</option>
                  <option value="FIRST">1ª instância</option>
                  <option value="SECOND">2ª instância</option>
                  <option value="SUPERIOR">Tribunal superior</option>
                </select>
              </Field>

              <Field label="UF" required={Boolean(form.city.trim())} hint="Informe a UF do órgão judicial; obrigatória ao preencher o município.">
                <input type="text" value={form.state} onChange={handleChange('state')} placeholder="Ex.: SP" maxLength={2} pattern="[A-Za-z]{2}" required={Boolean(form.city.trim())} />
              </Field>

              <Field label="Município" hint="Opcional. Informe para considerar os eventos locais aplicáveis.">
                <input type="text" value={form.city} onChange={handleChange('city')} placeholder="Ex.: Campinas" />
              </Field>

              <Field label="Órgão/Vara">
                <input type="text" value={form.courtDivision} onChange={handleChange('courtDivision')} placeholder="Vara, turma ou divisão" />
              </Field>

              <Field label="Tipo de comunicação">
                <input type="text" value={form.communicationType} onChange={handleChange('communicationType')} placeholder="Ex.: intimação" />
              </Field>
            </div>
          </section>

          <section className="rules-card deadline-form-card">
            <div className="rules-card-header">
              <div>
                <h3>Datas</h3>
                <p>Preencha as datas disponíveis para apoiar a identificação do termo inicial.</p>
              </div>
            </div>

            <div className="rules-card-body rules-form-grid three-columns">
              <Field label="Data de disponibilização">
                <input type="date" value={form.availabilityDate} onChange={handleChange('availabilityDate')} />
              </Field>

              <Field label="Data de publicação">
                <input type="date" value={form.publicationDate} onChange={handleChange('publicationDate')} />
              </Field>

              <Field label="Data de captura">
                <input type="date" value={form.captureDate} onChange={handleChange('captureDate')} />
              </Field>
            </div>
          </section>

          <section className="rules-card deadline-form-card">
            <div className="rules-card-header">
              <div>
                <h3>Texto da publicação/intimação</h3>
                <p>Cole o inteiro teor que será analisado diretamente pelo DeadlineEngine.</p>
              </div>
            </div>

            <div className="rules-card-body rules-form-grid">
              <Field label="Texto" required>
                <textarea
                  className={isTextMissing || isTextOverLimit ? 'is-invalid' : ''}
                  rows="14"
                  value={form.text}
                  onBlur={() => setTextTouched(true)}
                  onChange={handleChange('text')}
                  placeholder="Cole aqui o inteiro teor da comunicação ou publicação a ser analisada."
                  aria-invalid={isTextMissing || isTextOverLimit}
                />
              </Field>

              <div className="deadline-text-meta">
                <span className={`deadline-character-counter ${isTextOverLimit ? 'is-over-limit' : ''}`}>
                  {textLength.toLocaleString('pt-BR')} / {MAX_TEXT_LENGTH.toLocaleString('pt-BR')} caracteres
                </span>
                <span className="deadline-limit-warning">Limite máximo: {MAX_TEXT_LENGTH.toLocaleString('pt-BR')} caracteres.</span>
              </div>

              {isTextMissing ? <p className="deadline-field-error">O texto da publicação/intimação é obrigatório.</p> : null}
              {isTextOverLimit ? <p className="deadline-field-error">Reduza o texto para respeitar o limite máximo antes de simular.</p> : null}
            </div>
          </section>

          <section className="rules-card deadline-actions-card">
            <div className="rules-card-body deadline-form-actions">
              <div className="deadline-actions-note">
                <strong>Envio via gateway da API</strong>
                <span>Nenhum prazo será criado automaticamente; se houver revisão humana, você poderá enviar a correção para aprendizagem.</span>
              </div>
              <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-play'}`} />
                {loading ? 'Simulando...' : 'Simular prazo'}
              </button>
              <button type="button" className="btn btn-outline" onClick={handleReset} disabled={busy}>
                <i className="fas fa-eraser" /> Limpar
              </button>
            </div>
          </section>
          </fieldset>
          {evidenceFiles.length ? <section className="rules-card deadline-form-card">
            <div className="rules-card-header"><div><h3>Evidências desta sessão</h3>
              <p>Cada execução tem arquivo próprio. Guarde os downloads antes de sair da tela.</p></div></div>
            <div className="rules-card-body rules-form-grid">
              {evidenceFiles.map((evidence) => <button key={evidence.input.requestId} type="button" className="btn btn-outline"
                onClick={() => downloadSimulationEvidence(evidence)}>
                <i className="fas fa-download" /> Caso {evidence.input.caseId} — {evidence.outcome === 'VERIFIED' ? 'conferido' : 'falha registrada'}
                {' '}({evidence.response.data?.audit?.simulationId || evidence.input.requestId})
              </button>)}
            </div>
          </section> : null}
        </form>

        <DeadlineSimulationResult result={result} originalPayload={lastPayload} />
      </div>
    </div>
  )
}
